import { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Search, Calculator, Sparkles, Check, ArrowRight, DollarSign, Ruler,
  TrendingUp, Loader2, Copy, Menu, X, Lightbulb, PenTool, Image as ImageIcon, UploadCloud,

} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BookCreditCheckout } from "@/components/PricingModal";
import { PACKAGE_INCLUDES, PRICE } from "@/lib/pricing";

/* ------------------------------------------------------------------ shared */
export const KlyrLogo = ({ className = "w-9 h-7" }) => (
  <svg viewBox="0 0 120 84" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="klyrGold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F6DD9A" />
        <stop offset="0.55" stopColor="#D9B45C" />
        <stop offset="1" stopColor="#B4822E" />
      </linearGradient>
    </defs>
    <g stroke="url(#klyrGold)" strokeWidth="3" strokeLinecap="round" fill="none">
      <path d="M58 44 C 42 42 26 36 11 23" />
      <path d="M58 49 C 44 49 30 45 17 33" />
      <path d="M58 54 C 46 56 34 56 25 49" />
      <path d="M62 44 C 78 42 94 36 109 23" />
      <path d="M62 49 C 76 49 90 45 103 33" />
      <path d="M62 54 C 74 56 86 56 95 49" />
      <path d="M34 60 C 46 54 54 54 60 60 C 66 54 74 54 86 60" />
    </g>
    <path d="M60 8 C 55 20 55 30 60 40 C 65 30 65 20 60 8 Z" fill="url(#klyrGold)" />
  </svg>
);

export const KlyrMark = ({ onClick }) => (
  <button onClick={onClick} data-testid="klyr-logo" className="flex items-center gap-2.5 group">
    <KlyrLogo className="w-10 h-7 group-hover:opacity-90 transition-opacity" />
    <span className="font-ui font-semibold tracking-[0.18em] text-lg text-gold-100 leading-none">KLYR
      <span className="block text-[9px] tracking-[0.5em] text-gold-400/70 mt-0.5">STUDIO</span>
    </span>
  </button>
);

const NAV = [
  { key: "home", label: "Home" },
  { key: "features", label: "Features" },
  { key: "how-it-works", label: "How It Works" },
  { key: "examples", label: "Examples" },
  { key: "pricing", label: "Pricing" },
  { key: "faq", label: "FAQ" },
];

const FOOTER_NAV = [
  ["home", "Home"], ["features", "Features"], ["how-it-works", "How It Works"],
  ["examples", "Examples"], ["pricing", "Pricing"], ["faq", "FAQ"],
];

// Add real profile URLs here when the accounts exist; placeholders to bare domains are never shown.
const SOCIALS = [];

export const MarketingNav = ({ current, onNavigate, onHome, onGetStarted, onSignIn }) => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-navy-900/85 border-b border-gold-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <KlyrMark onClick={onHome} />
        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`nav-${n.key}`}
              onClick={() => (n.key === "home" ? onHome() : onNavigate(n.key))}
              className={`font-ui text-sm transition-colors ${current === n.key ? "text-gold-300" : "text-slate-300 hover:text-gold-200"}`}
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {onSignIn && (
            <button data-testid="nav-signin" onClick={onSignIn} className="hidden sm:block font-ui text-sm text-slate-200 hover:text-gold-200 transition-colors">Sign In</button>
          )}
          <button data-testid="nav-get-started" onClick={onGetStarted} className="gold-pill font-ui text-sm rounded-full px-4 sm:px-5 py-2 flex items-center gap-1.5 whitespace-nowrap">
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
          <button className="md:hidden text-slate-200" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-gold-500/10 bg-navy-900 px-4 py-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => { setOpen(false); n.key === "home" ? onHome() : onNavigate(n.key); }}
              className="text-left font-ui text-sm text-slate-200 hover:text-gold-200 py-2">{n.label}</button>
          ))}
          {onSignIn && (
            <button data-testid="nav-mobile-signin" onClick={() => { setOpen(false); onSignIn(); }}
              className="text-left font-ui text-sm font-semibold text-gold-200 hover:text-gold-100 py-2 border-t border-gold-500/10 mt-1 pt-3">Sign In</button>
          )}
        </div>
      )}
    </header>
  );
};

export const MarketingFooter = ({ onNavigate, onHome, onGetStarted }) => (
  <footer className="bg-navy-950 border-t border-gold-500/10 pt-12 pb-8 px-4 sm:px-8">
    <div className="max-w-7xl mx-auto flex flex-col gap-9">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          <KlyrMark onClick={onHome} />
          <p className="hidden sm:block font-ui text-sm text-slate-400 max-w-[210px] leading-snug border-l border-gold-500/15 pl-4">
            Your story deserves more than just an idea. It deserves KLYR.
          </p>
        </div>
        <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
          {FOOTER_NAV.map(([k, l]) => (
            <button key={k} data-testid={`footer-nav-${k}`}
              onClick={() => (k === "home" ? onHome() : onNavigate(k))}
              className="font-ui text-sm text-slate-300 hover:text-gold-200 transition-colors">{l}</button>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-4">
            {SOCIALS.map(({ Icon, label, href }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="text-slate-400 hover:text-gold-300 transition-colors">
                <Icon className="w-5 h-5" strokeWidth={1.6} />
              </a>
            ))}
          </div>
          {onGetStarted && (
            <button data-testid="footer-get-started" onClick={onGetStarted}
              className="gold-pill rounded-full px-5 py-2 font-ui text-sm flex items-center gap-1.5">
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="pt-6 border-t border-gold-500/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-ui text-xs text-slate-500">© {new Date().getFullYear()} KLYR Studio. All rights reserved.</p>
        <div className="flex flex-wrap items-center justify-center gap-6">
          {[["support", "Support"], ["careers", "Careers"], ["terms", "Terms"], ["privacy", "Privacy"], ["refund", "Refunds"], ["contact", "Contact"]].map(([k, l]) => (
            <button key={k} data-testid={`footer-${k}`} onClick={() => onNavigate(k)}
              className="font-ui text-xs text-slate-500 hover:text-gold-200 transition-colors">{l}</button>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

const Eyebrow = ({ children }) => <div className="eyebrow mb-3 flex items-center gap-2">{children}</div>;
const SectionTitle = ({ children, gold }) => (
  <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-50 leading-[1.12]">
    {children} {gold && <span className="gold-text italic font-normal">{gold}</span>}
  </h1>
);

/* ------------------------------------------------------- Market Research */
const MODE_LABEL = { nonfiction: "Non-fiction", fiction: "Fiction", children: "Children's", journal: "Journal", workbook: "Workbook", sudoku: "Puzzles", maze: "Puzzles", coloring: "Coloring", wordsearch: "Puzzles" };

const MarketResearchTool = ({ user, onGetStarted, onPick }) => {
  const [niche, setNiche] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!user) { onGetStarted(); return; }
    setLoading(true);
    try { setData(await api.marketResearch(niche)); }
    catch (e) { toast.error(apiError(e, "Market research failed.")); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <Eyebrow><TrendingUp className="w-3.5 h-3.5" /> Free Tool</Eyebrow>
        <SectionTitle gold="Right Now.">Find What Sells on Amazon</SectionTitle>
        <p className="font-ui text-base text-slate-400 mt-4">Discover the most profitable KDP book categories and ready-to-write ideas, backed by real market demand.</p>
      </div>
      <div className="mt-10 navy-card rounded-2xl p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input data-testid="mr-tool-input" value={niche} onChange={(e) => setNiche(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="A niche you like (e.g. fitness, finance, kids) — or leave blank"
              className="w-full bg-navy-950/60 border border-gold-500/20 rounded-xl pl-9 pr-3 py-3 font-ui text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-gold-400" />
          </div>
          <button data-testid="mr-tool-run" onClick={run} disabled={loading}
            className="gold-pill rounded-xl px-6 py-3 font-ui text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Analyzing…" : "Find profitable ideas"}
          </button>
        </div>
        {!user && <p className="font-ui text-xs text-slate-500 mt-3">Free to run — you'll be asked to create a free account first.</p>}

        {data && (
          <div className="mt-8 space-y-7" data-testid="mr-tool-results">
            <div>
              <div className="eyebrow mb-3">Top 5 profitable categories</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.categories.map((c, i) => (
                  <div key={i} className="rounded-xl border border-gold-500/15 bg-navy-800/50 p-4">
                    <div className="flex gap-2">
                      <span className="font-display text-2xl font-bold text-gold-400/80 leading-none">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="font-ui text-sm font-semibold text-slate-100">{c.name}</div>
                        <p className="font-ui text-xs text-slate-400 mt-1">{c.why}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <span className="font-ui text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Demand: {c.demand}</span>
                          <span className="font-ui text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-navy-700 text-slate-300">Comp: {c.competition}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="eyebrow mb-3">Books likely to sell</div>
              <div className="space-y-2">
                {data.suggestions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-gold-500/15 bg-navy-800/50 p-4">
                    <div className="min-w-0">
                      <div className="font-ui text-sm font-semibold text-slate-100 truncate">{s.title}</div>
                      <div className="font-ui text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-navy-700 text-slate-300">{MODE_LABEL[s.mode] || "Non-fiction"}</span>
                        <span className="truncate">{s.category}</span>
                      </div>
                    </div>
                    <button data-testid={`mr-tool-write-${i}`} onClick={() => (onPick ? onPick(s) : onGetStarted())}
                      className="shrink-0 flex items-center gap-1 font-ui text-xs font-medium text-gold-300 hover:translate-x-0.5 transition-transform">
                      Write this <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <ToolCrossLinks current="market-research" onNavigate={(k) => window.__klyrNav?.(k)} />
    </div>
  );
};

/* --------------------------------------------------------- Royalty calc */
const TRIMS = [
  { k: "5x8", w: 5, h: 8 }, { k: "5.25x8", w: 5.25, h: 8 }, { k: "5.5x8.5", w: 5.5, h: 8.5 },
  { k: "6x9", w: 6, h: 9 }, { k: "7x10", w: 7, h: 10 }, { k: "8x10", w: 8, h: 10 }, { k: "8.5x11", w: 8.5, h: 11 },
];
const printCost = (pages, ink) => {
  if (ink === "bw") return pages <= 108 ? 2.30 : 0.012 * pages;
  if (ink === "standard") return pages <= 108 ? 3.60 : 0.0255 * pages;
  return pages <= 40 ? 3.60 : 0.0625 * pages; // premium color
};
const money = (n) => `$${n.toFixed(2)}`;

const RoyaltyCalculator = () => {
  const [fmt, setFmt] = useState("paperback");
  const [price, setPrice] = useState(9.99);
  const [pages, setPages] = useState(120);
  const [ink, setInk] = useState("bw");

  let perSale = 0, cost = 0, rate = 0.6;
  if (fmt === "paperback") {
    cost = printCost(Number(pages) || 0, ink);
    perSale = Math.max(0, (Number(price) || 0) * 0.6 - cost);
  } else {
    rate = (price >= 2.99 && price <= 9.99) ? 0.70 : 0.35;
    perSale = Math.max(0, (Number(price) || 0) * rate);
  }
  const tooLow = fmt === "paperback" && (Number(price) || 0) * 0.6 < cost;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <Eyebrow><DollarSign className="w-3.5 h-3.5" /> Free Tool</Eyebrow>
        <SectionTitle gold="Before You Publish.">Know Your KDP Royalties</SectionTitle>
        <p className="font-ui text-base text-slate-400 mt-4">Estimate your Amazon KDP earnings per sale and monthly projections. US marketplace estimate.</p>
      </div>
      <div className="mt-10 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 navy-card rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="flex gap-2">
            {["paperback", "ebook"].map((f) => (
              <button key={f} data-testid={`royalty-fmt-${f}`} onClick={() => setFmt(f)}
                className={`flex-1 font-ui text-sm rounded-lg py-2.5 transition-colors ${fmt === f ? "gold-pill" : "bg-navy-800 text-slate-300 border border-gold-500/15"}`}>
                {f === "paperback" ? "Paperback" : "eBook"}
              </button>
            ))}
          </div>
          <Field label="List price (USD)">
            <input data-testid="royalty-price" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="calc-input" />
          </Field>
          {fmt === "paperback" && (
            <>
              <Field label="Page count">
                <input data-testid="royalty-pages" type="number" min="24" value={pages} onChange={(e) => setPages(e.target.value)} className="calc-input" />
              </Field>
              <Field label="Interior ink">
                <div className="grid grid-cols-3 gap-2">
                  {[["bw", "Black & White"], ["standard", "Standard Color"], ["premium", "Premium Color"]].map(([k, l]) => (
                    <button key={k} data-testid={`royalty-ink-${k}`} onClick={() => setInk(k)}
                      className={`font-ui text-xs rounded-lg py-2 transition-colors ${ink === k ? "bg-gold-400 text-navy-900 font-semibold" : "bg-navy-800 text-slate-300 border border-gold-500/15"}`}>{l}</button>
                  ))}
                </div>
              </Field>
            </>
          )}
        </div>
        <div className="md:col-span-2 navy-card rounded-2xl p-6 sm:p-8">
          <div className="eyebrow mb-4">Your royalty</div>
          {fmt === "paperback" && (
            <Row label="Printing cost" value={money(cost)} />
          )}
          <Row label={fmt === "ebook" ? `Royalty rate` : "Amazon share (40%)"} value={fmt === "ebook" ? `${Math.round(rate * 100)}%` : money((Number(price) || 0) * 0.4)} />
          <div className="border-t border-gold-500/15 my-3" />
          <div className="flex items-end justify-between">
            <span className="font-ui text-sm text-slate-300">Net per sale</span>
            <span data-testid="royalty-per-sale" className="font-display text-3xl font-bold gold-text">{money(perSale)}</span>
          </div>
          {tooLow && <p className="font-ui text-xs text-amber-300 mt-2">List price is below the minimum to cover printing. Raise your price.</p>}
          <div className="mt-6 space-y-2">
            {[50, 250, 1000].map((n) => (
              <div key={n} className="flex items-center justify-between font-ui text-sm">
                <span className="text-slate-400">{n} sales / mo</span>
                <span className="text-slate-100 font-semibold">{money(perSale * n)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="font-ui text-xs text-slate-500 text-center mt-6 max-w-2xl mx-auto">Estimates use published US KDP rates and may change. Always confirm final figures in your KDP dashboard.</p>
      <ToolCrossLinks current="royalty-calculator" onNavigate={(k) => window.__klyrNav?.(k)} />
    </div>
  );
};

/* ---------------------------------------------------------- Spine calc */
const SpineCalculator = () => {
  const [trimKey, setTrimKey] = useState("6x9");
  const [pages, setPages] = useState(120);
  const [paper, setPaper] = useState("white");
  const per = paper === "white" ? 0.002252 : paper === "cream" ? 0.0025 : 0.002347;
  const trim = TRIMS.find((t) => t.k === trimKey) || TRIMS[3];
  const spine = (Number(pages) || 0) * per;
  const bleed = 0.125;
  const fullW = trim.w * 2 + spine + bleed * 2;
  const fullH = trim.h + bleed * 2;
  const px = (inch) => Math.round(inch * 300);
  const dims = `Full cover: ${fullW.toFixed(3)} x ${fullH.toFixed(3)} in (${px(fullW)} x ${px(fullH)} px @300DPI) · Spine: ${spine.toFixed(3)} in`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <Eyebrow><Ruler className="w-3.5 h-3.5" /> Free Tool</Eyebrow>
        <SectionTitle gold="Done Right.">Cover & Spine Dimensions</SectionTitle>
        <p className="font-ui text-base text-slate-400 mt-4">Get the exact full-wrap cover size, spine width and pixel dimensions for your KDP paperback.</p>
      </div>
      <div className="mt-10 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-2 navy-card rounded-2xl p-6 sm:p-8 space-y-5">
          <Field label="Trim size">
            <select data-testid="spine-trim" value={trimKey} onChange={(e) => setTrimKey(e.target.value)} className="calc-input">
              {TRIMS.map((t) => <option key={t.k} value={t.k}>{t.w} x {t.h} in</option>)}
            </select>
          </Field>
          <Field label="Page count">
            <input data-testid="spine-pages" type="number" min="24" value={pages} onChange={(e) => setPages(e.target.value)} className="calc-input" />
          </Field>
          <Field label="Paper type">
            <div className="grid grid-cols-3 gap-2">
              {[["white", "White"], ["cream", "Cream"], ["color", "Color"]].map(([k, l]) => (
                <button key={k} data-testid={`spine-paper-${k}`} onClick={() => setPaper(k)}
                  className={`font-ui text-xs rounded-lg py-2 transition-colors ${paper === k ? "bg-gold-400 text-navy-900 font-semibold" : "bg-navy-800 text-slate-300 border border-gold-500/15"}`}>{l}</button>
              ))}
            </div>
          </Field>
        </div>
        <div className="md:col-span-3 navy-card rounded-2xl p-6 sm:p-8">
          <div className="eyebrow mb-4">Full cover wrap</div>
          <div className="flex items-stretch gap-1 h-40 mb-5">
            <div className="flex-1 bg-navy-800 border border-gold-500/20 rounded-l-lg flex items-center justify-center font-ui text-xs text-slate-400">Back<br/></div>
            <div className="bg-gold-400/80 flex items-center justify-center" style={{ width: `${Math.max(10, spine * 40)}px` }}>
              <span className="font-ui text-[9px] text-navy-900 font-bold" style={{ writingMode: "vertical-rl" }}>Spine</span>
            </div>
            <div className="flex-1 bg-navy-700 border border-gold-500/20 rounded-r-lg flex items-center justify-center font-ui text-xs text-slate-200">Front</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Spine width" value={`${spine.toFixed(3)} in`} />
            <Row label="Full width" value={`${fullW.toFixed(3)} in`} />
            <Row label="Full height" value={`${fullH.toFixed(3)} in`} />
            <Row label="Bleed" value={`${bleed} in`} />
            <Row label="Width (px@300)" value={`${px(fullW)} px`} />
            <Row label="Height (px@300)" value={`${px(fullH)} px`} />
          </div>
          <button data-testid="spine-copy" onClick={() => { navigator.clipboard?.writeText(dims); toast.success("Dimensions copied"); }}
            className="mt-5 w-full gold-pill rounded-xl py-2.5 font-ui text-sm flex items-center justify-center gap-2">
            <Copy className="w-4 h-4" /> Copy dimensions
          </button>
        </div>
      </div>
      <p className="font-ui text-xs text-slate-500 text-center mt-6">Based on KDP paper thickness (white 0.002252", cream 0.0025", color 0.002347" per page) + 0.125" bleed.</p>
      <ToolCrossLinks current="spine-calculator" onNavigate={(k) => window.__klyrNav?.(k)} />
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block font-ui text-xs font-semibold text-slate-300 mb-1.5">{label}</label>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="font-ui text-sm text-slate-400">{label}</span>
    <span className="font-ui text-sm text-slate-100 font-semibold">{value}</span>
  </div>
);
const ToolCrossLinks = ({ current, onNavigate }) => {
  const tools = [
    { k: "market-research", l: "Market Research", icon: TrendingUp },
    { k: "royalty-calculator", l: "Royalty Calculator", icon: DollarSign },
    { k: "spine-calculator", l: "Cover & Spine Calculator", icon: Ruler },
  ].filter((t) => t.k !== current);
  return (
    <div className="mt-12 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
      {tools.map((t) => (
        <button key={t.k} data-testid={`crosslink-${t.k}`} onClick={() => onNavigate(t.k)}
          className="navy-card rounded-xl p-4 flex items-center gap-3 text-left transition-colors">
          <t.icon className="w-5 h-5 text-gold-400" />
          <span className="font-ui text-sm text-slate-200">Try the {t.l}</span>
          <ArrowRight className="w-4 h-4 text-gold-400 ml-auto" />
        </button>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------- Pricing */
const PricingPage = ({ user, onGetStarted }) => (
  <div className="max-w-3xl mx-auto px-6 py-16 space-y-10" data-testid="pricing-page">
    <div className="space-y-3">
      <h1 className="font-display text-4xl sm:text-5xl text-slate-100">Pay per book. Everything included.</h1>
      <p className="font-read text-slate-400">One credit = one complete book with its Amazon KDP publishing package. No subscriptions and no add-ons. Your first 3 previews are free.</p>
    </div>
    <div className="rounded-2xl border border-gold-500/15 bg-navy-900/40 p-6">
      <BookCreditCheckout onNeedAuth={user ? null : onGetStarted} />
    </div>
    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="pricing-page-includes">
      {PACKAGE_INCLUDES.map((x) => (
        <li key={x} className="flex items-start gap-2 font-ui text-sm text-slate-300"><Check className="w-4 h-4 text-gold-300 mt-0.5 shrink-0" />{x}</li>
      ))}
    </ul>
    <p className="font-ui text-xs text-slate-500">Series Mode (${PRICE.series_unlock} per series) keeps characters and settings consistent across linked books.</p>
  </div>
);

const STAGES = [
  { l: "K", title: "Concept", icon: Lightbulb, desc: "Share your idea, choose your genre and audience, and set the direction. KLYR shapes it into a solid plan." },
  { l: "L", title: "Literature", icon: PenTool, desc: "KLYR writes and researches your full manuscript with structure, citations and a consistent human voice." },
  { l: "Y", title: "Your Book", icon: ImageIcon, desc: "Add original illustrations, design a standout cover, and format the interior exactly how you want it." },
  { l: "R", title: "Release", icon: UploadCloud, desc: "Download a publish-ready print PDF, EPUB and one-click KDP cover — or keep it for yourself." },
];

const HowItWorksPage = ({ onGetStarted }) => (
  <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
    <div className="text-center max-w-2xl mx-auto">
      <Eyebrow><Sparkles className="w-3.5 h-3.5" /> The KLYR Method</Eyebrow>
      <SectionTitle gold="One Powerful Process.">Four Stages.</SectionTitle>
      <p className="font-ui text-base text-slate-400 mt-4">KLYR takes you from a spark of inspiration to a finished, publish-ready book — step by step.</p>
    </div>
    <div className="mt-12 flex flex-wrap items-start justify-center gap-4 sm:gap-2">
      {STAGES.map((s, i) => (
        <div key={s.l} className="flex items-start">
          <div className="w-56 text-center px-3">
            <div className="mx-auto w-16 h-16 rounded-full border-2 border-gold-400 bg-navy-800 flex items-center justify-center mb-4">
              <span className="font-display text-2xl font-bold gold-text">{s.l}</span>
            </div>
            <div className="eyebrow mb-2 justify-center flex">{s.title}</div>
            <p className="font-ui text-sm text-slate-400">{s.desc}</p>
          </div>
          {i < STAGES.length - 1 && <ArrowRight className="hidden sm:block w-5 h-5 text-gold-400/60 mt-6 shrink-0" />}
        </div>
      ))}
    </div>
    <div className="mt-14 navy-card rounded-2xl p-8 text-center max-w-3xl mx-auto">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="font-display text-3xl font-bold text-slate-200">120 hrs</div>
          <div className="font-ui text-xs text-slate-500 mt-1">Traditional self-publishing</div>
        </div>
        <div>
          <div className="font-display text-3xl font-bold gold-text">~12 min</div>
          <div className="font-ui text-xs text-slate-500 mt-1">With KLYR Studio</div>
        </div>
      </div>
      <button data-testid="hiw-cta" onClick={onGetStarted} className="gold-pill rounded-full px-7 py-3 font-ui text-sm mt-7 inline-flex items-center gap-2">
        Start creating now <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

/* ------------------------------------------------------------- Examples */
const EXAMPLES = [
  { src: "/samples/3228f6ae64deca48864aac73adabbaf7f890b32b5e5ca19d60add104c630eb85.jpeg", genre: "Fiction", title: "The Last Horizon", author: "by J. Halvorsen", blurb: "A sweeping literary novel about one choice that changes everything — written chapter by chapter with a consistent human voice.", meta: "260 pages · 6×9 · KDP-ready" },
  { src: "/samples/fd09b1a974a2e9ea630a467c10e5dd93a3f4a84bf837bdda5f96089face32778.jpeg", genre: "Non-fiction", title: "The Mindset Shift", author: "by Dr. A. Okafor", blurb: "A research-backed self-help guide with original frameworks, case studies, APA citations and a fill-in toolkit appendix.", meta: "180 pages · citations · worksheets" },
  { src: "/samples/c25316ce7c9e70a76837f8117edb6294b8cd8cede0b9cff1588bddb2cada1a02.jpeg", genre: "Children's", title: "Luna's Brave Heart", author: "by The KLYR Studio", blurb: "A warm illustrated picture book with a gentle arc and custom artwork on every spread — ready for little hands.", meta: "32 pages · full-color · 8.5×8.5" },
  { src: "/samples/cc065efb21438712a5ad2a583d2e7e13f5f502820c78b219ad7b5e9d33200d85.jpeg", genre: "Activity & Puzzle", title: "Brain Boost Puzzles", author: "100+ puzzles", blurb: "A print-ready puzzle book with auto-generated sudoku, mazes and word searches — plus a complete answer key.", meta: "120 pages · answer key · 8.5×11" },
  { src: "/samples/2fec99ea10693800f6e455521d14054af1628bde47dbf8d23aff5d6c6b752b94.jpeg", genre: "Cookbook", title: "Simple Healthy Meals", author: "by Eliza Reed", blurb: "A clean, elegantly formatted cookbook with structured recipes, intros and a polished interior layout.", meta: "140 pages · formatted · 7×10" },
  { src: "/samples/07a4af0cc05d6f60411b88fe2c62fba31db4d42bc0b0c1ee44c317083cba6ee7.jpeg", genre: "Romance", title: "Letters to the Sea", author: "by Aurora Gale", blurb: "A tender romance with a cinematic conclusion, recurring characters and a cover that sells the story at a glance.", meta: "240 pages · 5×8 · KDP-ready" },
];

const ExamplesPage = ({ onGetStarted }) => (
  <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
    <div className="text-center max-w-2xl mx-auto">
      <Eyebrow><BookOpen className="w-3.5 h-3.5" /> Made with KLYR</Eyebrow>
      <SectionTitle gold="Made in Minutes.">Real Books,</SectionTitle>
      <p className="font-ui text-base text-slate-400 mt-4">
        Every one of these — cover, manuscript, interior and KDP files — was created inside KLYR Studio.
        Browse a few before you start your own.
      </p>
    </div>

    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
      {EXAMPLES.map((b, i) => (
        <motion.div
          key={b.title}
          data-testid={`example-card-${i}`}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
          whileHover={{ y: -6 }}
          className="group navy-card rounded-2xl overflow-hidden flex flex-col"
        >
          <div className="relative overflow-hidden">
            <img src={b.src} alt={`${b.title} — ${b.genre} book cover`} className="w-full aspect-[2/3] object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            <span className="absolute top-3 left-3 font-ui text-[10px] uppercase tracking-wider text-navy-900 bg-gold-400 rounded-full px-2.5 py-1 font-semibold">{b.genre}</span>
          </div>
          <div className="p-5 flex flex-col flex-1">
            <h3 className="font-display text-xl font-semibold text-slate-50 leading-snug">{b.title}</h3>
            <div className="font-ui text-xs text-gold-300/80 mt-1">{b.author}</div>
            <p className="font-read text-sm text-slate-400 mt-3 leading-relaxed flex-1">{b.blurb}</p>
            <div className="mt-4 pt-3 border-t border-gold-500/10 font-ui text-[11px] uppercase tracking-wide text-slate-500">{b.meta}</div>
          </div>
        </motion.div>
      ))}
    </div>

    <div className="mt-16 navy-card rounded-2xl p-8 sm:p-10 text-center max-w-3xl mx-auto">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">Your book could be next.</h2>
      <p className="font-ui text-base text-slate-400 mt-3 max-w-xl mx-auto">Pick a genre, describe your idea, and watch KLYR build a publish-ready book — covers and all.</p>
      <button data-testid="examples-cta" onClick={onGetStarted} className="gold-pill rounded-full px-7 py-3.5 font-ui text-sm mt-7 inline-flex items-center gap-2">
        Create yours now <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

/* ------------------------------------------------------------- router */
const PAGES = {
  "market-research": MarketResearchTool,
  "royalty-calculator": RoyaltyCalculator,
  "spine-calculator": SpineCalculator,
  "pricing": PricingPage,
  "how-it-works": HowItWorksPage,
  "examples": ExamplesPage,
};

export const isMarketingPage = (key) => !!PAGES[key];

export const MarketingSite = ({ initialPage, user, onHome, onGetStarted, onSignIn, onCheckout, onPick, onNavigateOther }) => {
  const [page, setPage] = useState(initialPage || "market-research");
  const navigate = (k) => {
    if (k === "features") { onHome?.(); return; } // features is a homepage section
    if (PAGES[k]) { setPage(k); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else onNavigateOther?.(k); // faq/terms/privacy/about/contact handled by InfoPage
  };
  window.__klyrNav = navigate;
  const Page = PAGES[page] || MarketResearchTool;
  return (
    <div className="min-h-screen bg-navy-900">
      <MarketingNav current={page} onNavigate={navigate} onHome={onHome} onGetStarted={onGetStarted} onSignIn={onSignIn} />
      <main>
        <Page user={user} onGetStarted={onGetStarted} onCheckout={onCheckout} onPick={onPick} />
      </main>
      <MarketingFooter onNavigate={navigate} onHome={onHome} onGetStarted={onGetStarted} />
    </div>
  );
};
